import { fetchStatus, ApiError } from "../api/client";
import { Alert } from "../components/Alert";
import { Spinner } from "../components/Spinner";
import { StatusBadge } from "../components/StatusBadge";
import { useAsyncData } from "../hooks/useAsyncData";
import { formatError } from "../utils/formatError";

/** Human explanation for the two "pending" states that a bare badge doesn't make obvious. */
function explainPendingState(state: string): string | null {
  if (state === "system_never_indexed") {
    return (
      "No domain in this deployment has been indexed yet. Search results are unavailable " +
      "everywhere until indexing runs at least once."
    );
  }
  if (state === "domain_never_indexed") {
    return (
      "This domain has not been indexed yet, even though other domains have. It will show no " +
      "results until it is indexed."
    );
  }
  return null;
}

function formatDate(value: string | null): string {
  if (!value) {
    return "never";
  }
  return new Date(value).toLocaleString();
}

export function StatusScreen(): JSX.Element {
  const { data: report, loading, error, reload } = useAsyncData(() => fetchStatus(), []);

  if (loading) {
    return <Spinner label="Loading index status…" />;
  }

  if (error instanceof ApiError && error.isServiceUnavailable) {
    // Deliberately not an error banner: the index opening is an expected, transient state, and
    // rendering it as a failure would tell an operator something is broken when it isn't.
    return (
      <div className="status-screen">
        <Alert variant="info">
          The index is not ready yet ({error.detail || "still opening"}). This is expected shortly
          after the service starts.
        </Alert>
        <button type="button" className="button button--secondary" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="status-screen">
        <Alert variant="error">Could not load index status: {formatError(error)}</Alert>
        <button type="button" className="button button--secondary" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  if (!report) {
    return <Alert variant="error">No status data was returned.</Alert>;
  }

  return (
    <div className="status-screen">
      <div className="status-screen__totals">
        <div className="stat-tile">
          <span className="stat-tile__value">{report.files_total.toLocaleString()}</span>
          <span className="stat-tile__label">Files indexed, all domains</span>
        </div>
        <div className="stat-tile">
          <span className="stat-tile__value">{report.domains.length.toLocaleString()}</span>
          <span className="stat-tile__label">Known domains</span>
        </div>
      </div>

      {report.domains.length === 0 ? (
        <Alert variant="info">
          No domains are known to the index yet. This means nothing has been searched, not that a
          search found nothing.
        </Alert>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Domain</th>
              <th scope="col">Status</th>
              <th scope="col">Files indexed</th>
              <th scope="col">Last indexed</th>
              <th scope="col">Detail</th>
            </tr>
          </thead>
          <tbody>
            {report.domains.map((domain) => {
              const explanation = explainPendingState(domain.status.state);
              return (
                <tr key={domain.domain}>
                  <td>{domain.label || domain.domain}</td>
                  <td>
                    <StatusBadge state={domain.status.state} />
                  </td>
                  <td>
                    {domain.status.files_indexed.toLocaleString()} /{" "}
                    {domain.status.files_total.toLocaleString()}
                  </td>
                  <td>{formatDate(domain.status.last_indexed_at)}</td>
                  <td>{explanation ?? (domain.status.notice || "—")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="status-screen__generated-at">
        Generated at {report.generated_at ? formatDate(report.generated_at) : "unknown"}.{" "}
        <button type="button" className="link-button" onClick={reload}>
          Refresh
        </button>
      </p>
    </div>
  );
}
